import { EntireComposerState } from './composer';
import { Vector2D } from '../../types/common';
import { Rectangle } from 'ark-common';
import { ClipbookState } from '../types';
import { drawingAreaMargin } from '../../drawing/constants';

const minZoomRatioIfSmallEnough = 0.1;

export interface ComposerViewState {
  readonly zoom: number;
  readonly pan: Vector2D;
}

export interface ComposerViewParams {
  readonly clientWidth: number;
  readonly clientHeight: number;
  readonly contentWidth: number;
  readonly contentHeight: number;
}

export function composerViewParamsFromPageSize(
  clientRect: Rectangle,
  width: number,
  height: number,
): ComposerViewParams {
  return {
    clientWidth: clientRect.width,
    clientHeight: clientRect.height,
    contentWidth: width,
    contentHeight: height,
  };
}

export function initialComposerZoom(params: ComposerViewParams) {
  const { contentWidth, contentHeight } = params;

  if (contentWidth === 0 || contentHeight === 0) {
    return 1;
  }

  return Math.min(params.clientWidth / contentWidth, params.clientHeight / contentHeight);
}

export function initialComposerPan(params: ComposerViewParams, zoom: number): Vector2D {
  return {
    x: Math.floor(params.clientWidth - zoom * params.contentWidth) / 2,
    y: Math.floor(params.clientHeight - zoom * params.contentHeight) / 2,
  };
}

export function minComposerZoomRatio(clipbook: ClipbookState, clientRect: Rectangle) {
  const { pages, currentPage } = clipbook;
  if (currentPage === null) {
    throw new Error('Illegal state.');
  }

  const { width, height } = pages[currentPage];
  const viewParams = composerViewParamsFromPageSize(clientRect, width, height);

  return Math.min(initialComposerZoom(viewParams), minZoomRatioIfSmallEnough);
}

export const defaultViewState: ComposerViewState = { zoom: 1, pan: { x: 0, y: 0 } };

// Copied from utils/view.
function composerPanningRange(
  params: ComposerViewParams,
  zoom: number,
  margin: number,
): [Vector2D, Vector2D] {
  const scaledMargin = margin * zoom;

  const { clientWidth, clientHeight, contentWidth, contentHeight } = params;
  const marginBoxWidth = contentWidth * zoom + scaledMargin * 2;
  const marginBoxHeight = contentHeight * zoom + scaledMargin * 2;

  const diffWidth = clientWidth - marginBoxWidth;
  const diffHeight = clientHeight - marginBoxHeight;
  const overflowsX = diffWidth < 0;
  const overflowsY = diffHeight < 0;

  const minX = (overflowsX ? diffWidth : diffWidth / 2) + scaledMargin;
  const minY = (overflowsY ? diffHeight : diffHeight / 2) + scaledMargin;
  const maxX = (overflowsX ? 0 : diffWidth / 2) + scaledMargin;
  const maxY = (overflowsY ? 0 : diffHeight / 2) + scaledMargin;

  return [{ x: minX, y: minY }, { x: maxX, y: maxY }];
}

export function normalizeComposerPan(
  viewParams: ComposerViewParams,
  zoom: number,
  pan: Vector2D,
): Vector2D {
  const [min, max] = composerPanningRange(viewParams, zoom, drawingAreaMargin);
  return {
    x: Math.max(min.x, Math.min(pan.x, max.x)),
    y: Math.max(min.y, Math.min(pan.y, max.y)),
  };
}

export function resetView(state: EntireComposerState): EntireComposerState {
  if (state.clipbook === null) {
    return { ...state, view: defaultViewState };
  }

  const { pages, currentPage } = state.clipbook;
  if (currentPage === null) {
    return { ...state, view: defaultViewState };
  }

  const { width, height } = pages[currentPage];
  const viewParams = composerViewParamsFromPageSize(state.clientRect, width, height);
  const zoom = initialComposerZoom(viewParams);
  const pan = initialComposerPan(viewParams, zoom);
  return { ...state, view: { zoom, pan } };
}

export function zoomTo(state: EntireComposerState, nextZoom: number): EntireComposerState {
  if (state.clipbook === null) {
    throw new Error('Illegal state.');
  }

  const { pages, currentPage } = state.clipbook;
  if (currentPage === null) {
    throw new Error('Illegal state.');
  }

  const { zoom, pan } = state.view;
  const { clientRect } = state;
  const lookingAt = {
    x: (-pan.x + 0.5 * clientRect.width) / zoom,
    y: (-pan.y + 0.5 * clientRect.height) / zoom,
  };
  const nextPanBeforeClamping = {
    x: -(lookingAt.x * nextZoom - 0.5 * clientRect.width),
    y: -(lookingAt.y * nextZoom - 0.5 * clientRect.height),
  };

  const { width, height } = pages[currentPage];
  const viewParams = composerViewParamsFromPageSize(clientRect, width, height);
  const nextPan = normalizeComposerPan(viewParams, nextZoom, nextPanBeforeClamping);

  return { ...state, view: { zoom: nextZoom, pan: nextPan } };
}

export function setPan(state: EntireComposerState, pan: Vector2D): EntireComposerState {
  return { ...state, view: { ...state.view, pan } };
}
